<script lang="ts">
  import Breadcrumb from '../shared/Breadcrumb.svelte';
  import EnvSwitcher from '../shared/EnvSwitcher.svelte';
  import { fetchHealthData, type ServiceHealth, type HealthStatus } from '../../services/health-client';

  let env = $state<'Beta' | 'Prod'>('Beta');
  let services = $state<ServiceHealth[]>([]);
  let loading = $state(true);

  async function load() {
    loading = true;
    try {
      services = await fetchHealthData(env);
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    env;
    load();
  });

  const overallStatus = $derived.by<HealthStatus>(() => {
    if (services.some((s) => s.status === 'unhealthy')) return 'unhealthy';
    if (services.some((s) => s.status === 'degraded')) return 'degraded';
    return 'healthy';
  });

  const statusConfig: Record<HealthStatus, { color: string; bg: string; label: string; icon: string }> = {
    healthy: { color: 'text-success', bg: 'bg-success', label: 'All Systems Operational', icon: '✓' },
    degraded: { color: 'text-warning', bg: 'bg-warning', label: 'Partial Degradation', icon: '⚠' },
    unhealthy: { color: 'text-error', bg: 'bg-error', label: 'Service Disruption', icon: '✕' },
  };

  const cardBorder: Record<HealthStatus, string> = {
    healthy: 'border-success/30 hover:border-success/60',
    degraded: 'border-warning/30 hover:border-warning/60',
    unhealthy: 'border-error/30 hover:border-error/60',
  };
</script>

<div class="min-h-screen bg-bg-dark text-text-primary">
  <div class="w-full px-6 py-4">
    <div class="flex items-center justify-between pr-20">
      <Breadcrumb items={[{ label: 'Health Dashboard' }]} />
      <EnvSwitcher bind:value={env} />
    </div>

    <div class="mt-4 mb-6">
      <h1 class="text-2xl font-bold">📊 Health Dashboard</h1>
      <p class="mt-1 text-sm text-text-secondary">Aggregate health view across all Campaign Strategist services</p>
    </div>

    {#if loading}
      <div class="h-20 rounded-xl bg-bg-panel animate-skeleton-pulse mb-6"></div>
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {#each Array(6) as _, i (i)}
          <div class="h-40 rounded-xl bg-bg-panel animate-skeleton-pulse"></div>
        {/each}
      </div>
    {:else}
      <!-- Overall Status Banner -->
      {@const cfg = statusConfig[overallStatus]}
      <div class="bg-bg-panel border border-border rounded-xl p-5 mb-6 flex items-center gap-4">
        <div class="w-12 h-12 rounded-full {cfg.bg}/15 flex items-center justify-center">
          <span class="text-xl {cfg.color}">{cfg.icon}</span>
        </div>
        <div>
          <h2 class="text-lg font-semibold {cfg.color}">{cfg.label}</h2>
          <p class="text-xs text-text-muted">{env} environment · {services.filter((s) => s.status === 'healthy').length}/{services.length} services healthy</p>
        </div>
        <div class="ml-auto flex gap-1">
          {#each services as s (s.service)}
            <div class="w-3 h-3 rounded-full {statusConfig[s.status].bg}" title="{s.service}: {s.status}"></div>
          {/each}
        </div>
      </div>

      <!-- Service Cards -->
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {#each services as svc (svc.service)}
          {@const sc = statusConfig[svc.status]}
          <div class="bg-bg-panel border rounded-xl p-5 transition-colors {cardBorder[svc.status]}">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <span class="text-xl">{svc.icon}</span>
                <h3 class="font-semibold text-sm">{svc.service}</h3>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="w-2.5 h-2.5 rounded-full {sc.bg} {svc.status !== 'healthy' ? 'animate-pulse-orange' : ''}"></span>
                <span class="text-xs font-medium {sc.color} capitalize">{svc.status}</span>
              </div>
            </div>
            <div class="bg-bg-dark/50 rounded-lg p-4 text-center">
              <p class="text-2xl font-bold {sc.color}">{svc.metricValue}</p>
              <p class="text-xs text-text-muted mt-1">{svc.metric}</p>
            </div>
            <p class="text-[10px] text-text-muted mt-3 text-right">
              Checked: {new Date(svc.lastCheck).toLocaleTimeString()}
            </p>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>
